import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Table,
  Button,
  Pagination,
  Alert,
  Spinner,
  Badge,
} from 'react-bootstrap';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaMoneyBillWave,
  FaSearch,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { renderAccountWithTooltip } from '../components/AccountTooltip';

const getAuthToken = () => localStorage.getItem('authToken');

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

const formatAmount = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 50;
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayments(currentPage);
  }, [currentPage]);

  const fetchPayments = async (page) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-payments?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setPayments(response.data.payments || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setAlert({
        show: true,
        message: 'Error loading payments. Please try again.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}delete-payment`,
        { payment_id: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message: response.data.message || 'Payment deleted successfully!',
        variant: 'success',
      });
      fetchPayments(currentPage);
    } catch (error) {
      console.error('Error deleting payment:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error deleting payment. Please try again.',
        variant: 'danger',
      });
    }
  };

  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.payment_number?.toString().toLowerCase().includes(search) ||
      payment.from_account?.toLowerCase().includes(search) ||
      payment.to_account?.toLowerCase().includes(search) ||
      payment.description?.toLowerCase().includes(search)
    );
  });

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '18px 10px',
        }}
      >
        {/* Compact Top Bar (like ReceiptList) */}
        <div
          className="compact-top-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '14px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '7px 10px',
            borderRadius: '7px',
            color: 'white',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <FaMoneyBillWave style={{ fontSize: '1.3rem', marginRight: 6 }} />
            <span
              style={{
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: 0.5,
              }}
            >
              Payments
            </span>
            <div style={{ position: 'relative', width: 220 }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#adb5bd',
                  fontSize: '1rem',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px 6px 32px',
                  border: '1px solid #495057',
                  borderRadius: '5px',
                  fontSize: '0.95rem',
                  outline: 'none',
                  background: '#f8f9fa',
                  color: '#23272b',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#667eea')}
                onBlur={(e) => (e.target.style.borderColor = '#495057')}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              variant="light"
              size="sm"
              onClick={() => navigate('/payment')}
              aria-label="New Payment"
              style={{
                borderRadius: '6px',
                width: '34px',
                height: '34px',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="New Payment"
            >
              <FaPlus />
            </Button>
          </div>
        </div>
        {/* Floating Alert (bottom left) */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              position: 'fixed',
              left: '18px',
              bottom: '18px',
              zIndex: 9999,
              minWidth: '180px',
              maxWidth: '320px',
              pointerEvents: 'auto',
            }}
          >
            {alert.message}
          </Alert>
        )}
        {/* Table Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflowX: 'auto',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Loading payments...
              </p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#6c757d',
              }}
            >
              <FaMoneyBillWave
                style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '15px' }}
              />
              <p style={{ fontSize: '1.1rem', margin: 0 }}>
                {searchTerm
                  ? 'No payments found matching your search'
                  : 'No payments available'}
              </p>
            </div>
          ) : (
            <>
              <Table hover responsive style={{ marginBottom: 0 }}>
                <thead
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6',
                  }}
                >
                  <tr>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        whiteSpace: 'nowrap',
                        fontSize: '0.92rem',
                      }}
                    >
                      Date
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        whiteSpace: 'nowrap',
                        fontSize: '0.92rem',
                        width: 90,
                        minWidth: 70,
                        maxWidth: 110,
                        textAlign: 'center',
                      }}
                    >
                      No.
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        fontSize: '0.92rem',
                      }}
                    >
                      From Account
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        fontSize: '0.92rem',
                      }}
                    >
                      To Account
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        textAlign: 'right',
                        fontSize: '0.92rem',
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        fontSize: '0.92rem',
                      }}
                    >
                      Description
                    </th>
                    <th
                      style={{
                        padding: '6px 8px',
                        fontWeight: 600,
                        color: '#495057',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        fontSize: '0.92rem',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr
                      key={payment.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f8f9ff')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <td
                        style={{
                          padding: '6px 8px',
                          whiteSpace: 'nowrap',
                          color: '#495057',
                          fontSize: '0.97rem',
                        }}
                      >
                        {formatDisplayDate(payment.date)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        <Badge
                          bg="secondary"
                          style={{
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            padding: '3px 7px',
                            borderRadius: '5px',
                            letterSpacing: 0.5,
                          }}
                        >
                          #{payment.payment_number}
                        </Badge>
                      </td>
                      <td style={{ padding: '6px 8px', color: '#495057' }}>
                        {renderAccountWithTooltip(
                          payment.from_account,
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {payment.from_account}
                          </span>,
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#495057' }}>
                        {renderAccountWithTooltip(
                          payment.to_account,
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: 140,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {payment.to_account}
                          </span>,
                        )}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'right',
                          fontWeight: 600,
                          color: '#dc3545',
                          fontSize: '0.97rem',
                        }}
                      >
                        {formatAmount(payment.amount)}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          color: '#6c757d',
                          maxWidth: 140,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.95rem',
                        }}
                      >
                        {payment.description || '-'}
                      </td>
                      <td
                        style={{
                          padding: '6px 8px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            navigate(`/edit-payment/${payment.id}`)
                          }
                          style={{
                            borderRadius: '6px',
                            padding: 0,
                            width: 32,
                            height: 32,
                            minWidth: 0,
                            minHeight: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 4,
                          }}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete Payment #${payment.payment_number}?`,
                              )
                            ) {
                              handleDelete(payment.id);
                            }
                          }}
                          style={{
                            borderRadius: '6px',
                            padding: 0,
                            width: 32,
                            height: 32,
                            minWidth: 0,
                            minHeight: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {/* Smart Pagination: show first, last, 5 before/after current */}
              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: '25px',
                    paddingTop: '20px',
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Pagination style={{ margin: 0 }}>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {totalPages <= 13 ? (
                      [...Array(totalPages)].map((_, index) => (
                        <Pagination.Item
                          key={index}
                          active={index + 1 === currentPage}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))
                    ) : (
                      <>
                        <Pagination.Item
                          active={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </Pagination.Item>
                        {currentPage > 6 && <Pagination.Ellipsis disabled />}
                        {Array.from(
                          { length: 11 },
                          (_, i) => currentPage - 5 + i,
                        )
                          .filter((page) => page > 1 && page < totalPages)
                          .map((page) => (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          ))}
                        {currentPage < totalPages - 5 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          active={currentPage === totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Pagination.Item>
                      </>
                    )}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PaymentList;
